import { records } from "./workspace.js";

// A per-session cache: no timers, no stored SAS URLs, no automatic retry loop.
export class MediaPreviews {
  urls = new Map();
  errors = new Map();
  attempted = new Set();
  versions = new Map();
  generation = 0;
  busy = false;

  clear() {
    this.generation += 1;
    this.urls.clear();
    this.errors.clear();
    this.attempted.clear();
    this.versions.clear();
    this.busy = false;
  }

  retryFailed() {
    for (const id of this.errors.keys()) this.attempted.delete(id);
    this.errors.clear();
  }

  forget(assetId) {
    const id = String(assetId);
    this.versions.set(id, (this.versions.get(id) ?? 0) + 1);
    this.urls.delete(id);
    this.errors.delete(id);
    this.attempted.delete(id);
  }

  async load(assets, downloads, decode) {
    if (this.busy) return null;
    const ids = [...new Set(records(assets).map(asset => String(asset.assetId ?? asset.id ?? "")))]
      .filter(id => id && !this.urls.has(id) && !this.attempted.has(id));
    if (!ids.length) return null;
    ids.forEach(id => this.attempted.add(id));
    const generation = this.generation;
    const versions = new Map(ids.map(id => [id, this.versions.get(id) ?? 0]));
    const current = id => versions.get(id) === (this.versions.get(id) ?? 0);
    this.busy = true;
    try {
      const descriptors = new Map(records(await downloads()).map(item => [String(item.assetId), item]));
      for (const id of ids) {
        if (generation !== this.generation) return null;
        if (!current(id)) continue;
        try {
          const item = descriptors.get(id);
          if (!item?.downloadUrl) throw new Error("Preview download is not available.");
          const url = await decode(item.downloadUrl);
          if (generation !== this.generation) return null;
          if (!current(id)) continue;
          this.urls.set(id, url);
        } catch {
          // Keep failures independent and do not expose private download URLs in UI errors.
          if (generation !== this.generation) return null;
          if (!current(id)) continue;
          this.errors.set(id, "Preview unavailable");
        }
      }
    } catch {
      if (generation !== this.generation) return null;
      ids.filter(current).forEach(id => this.errors.set(id, "Preview unavailable"));
    } finally {
      if (generation === this.generation) this.busy = false;
    }
    return { failed: ids.filter(id => this.errors.has(id)).length };
  }
}
